import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  getSignupWeekOverview, createSignupPick, deleteSignupPick, copySignupWeek,
  getStaff, getAdvisoryGroups,
} from "../../lib/api";
import OptionEditorDialog, { SlotManager } from "./OptionEditorDialog";
import { weekDates, prettyDate, weekWindowOptions, mondayOf, isoDate, tripAttendsDate } from "./signupHelpers";
import { useAuth } from "../../App";

export default function SignUpsTab() {
  const { user } = useAuth();
  const [monday, setMonday] = useState(isoDate(mondayOf(new Date())));
  const [overview, setOverview] = useState({ slots: [], options: [], picks: [], students: [], trips: [], trip_picks: [] });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid"); // grid | wizard
  const [advisoryFilter, setAdvisoryFilter] = useState(user?.advisory_group || "all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [editorDefaults, setEditorDefaults] = useState({});
  const [slotMgrOpen, setSlotMgrOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [advisoryGroups, setAdvisoryGroups] = useState([]); // configured advisory list (with names)

  const dates = useMemo(() => {
    // Hide any day that has already passed (even in past weeks) — Advisory Sign-Ups
    // should only show today + upcoming days.
    const today = isoDate(new Date());
    return weekDates(monday).filter((d) => d >= today);
  }, [monday]);

  // Pull the real, configured advisory list (names like "Advisory 1 Green Room") once.
  useEffect(() => {
    getAdvisoryGroups()
      .then((res) => setAdvisoryGroups([...(res.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
      .catch(() => {});
  }, []);

  const students = useMemo(() => {
    const key = (s) => (s.preferred_name || s.first_name || "").toLowerCase();
    return [...(overview.students || [])].sort((a, b) => key(a).localeCompare(key(b)));
  }, [overview.students]);

  const load = async () => {
    try {
      const [ov, stf] = await Promise.all([
        getSignupWeekOverview(monday, advisoryFilter === "all" ? null : advisoryFilter),
        getStaff({ assignable: true }),
      ]);
      setOverview(ov.data);
      setStaff([...(stf.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch {
      toast.error("Failed to load sign-ups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [monday, advisoryFilter]);

  // Quickly look up picks: key = `${student_id}|${slot_id}|${date}`
  const pickIndex = useMemo(() => {
    const map = {};
    (overview.picks || []).forEach((p) => {
      const k = `${p.student_id}|${p.slot_id}|${p.date}`;
      map[k] = p;
    });
    return map;
  }, [overview.picks]);

  // Options grouped by slot+date
  const optionsByCell = useMemo(() => {
    const map = {};
    (overview.options || []).forEach((o) => {
      const k = `${o.slot_id}|${o.date}`;
      (map[k] = map[k] || []).push(o);
    });
    return map;
  }, [overview.options]);

  // Students who are on an all-day trip on a given date: key = date, value = Set(student_id)
  const tripDays = useMemo(() => {
    const tripById = {};
    (overview.trips || []).forEach((t) => { tripById[t.id] = t; });
    const map = {};
    (overview.trip_picks || []).forEach((p) => {
      const trip = tripById[p.option_id];
      if (!trip) return;
      (trip.dates || []).forEach((d) => {
        if (tripAttendsDate(p, trip, d)) {
          (map[d] = map[d] || new Set()).add(p.student_id);
        }
      });
    });
    return map; // { date: Set(student_id) }
  }, [overview.trips, overview.trip_picks]);

  const tripTitlesFor = (studentId, date) => {
    const tripById = {};
    (overview.trips || []).forEach((t) => { tripById[t.id] = t; });
    return (overview.trip_picks || [])
      .filter((p) => p.student_id === studentId && tripById[p.option_id] && tripAttendsDate(p, tripById[p.option_id], date))
      .map((p) => tripById[p.option_id]?.title)
      .filter(Boolean)
      .join(", ");
  };

  const countForOption = (opt) => {
    if (opt.type !== "block") return 0;
    return (overview.picks || []).filter((p) => p.option_id === opt.id && p.date === opt.date).length;
  };

  const handlePick = async (studentId, slotId, date, optionId, currentPick) => {
    try {
      if (!optionId || optionId === "none") {
        if (currentPick) {
          await deleteSignupPick(currentPick.id);
        }
      } else {
        try {
          await createSignupPick({ option_id: optionId, student_id: studentId, date });
        } catch (e) {
          if (e.response?.status === 409) {
            const proceed = window.confirm(e.response?.data?.detail + "\n\nAdd anyway?");
            if (!proceed) return;
            await createSignupPick({ option_id: optionId, student_id: studentId, date, override_capacity: true });
          } else {
            throw e;
          }
        }
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update pick");
    }
  };

  const handleAddOption = (slot_id = "", date = "") => {
    setEditingOption(null);
    setEditorDefaults({ slot_id, date });
    setEditorOpen(true);
  };

  const handleEditOption = (opt) => {
    setEditingOption(opt);
    setEditorDefaults({});
    setEditorOpen(true);
  };

  const handleCopyLastWeek = async () => {
    const prev = new Date(monday + "T00:00:00");
    prev.setDate(prev.getDate() - 7);
    try {
      const res = await copySignupWeek({ source_date: isoDate(prev), target_date: monday, include_staff: true });
      toast.success(`Copied ${res.data.copied} options from last week`);
      load();
      setCopyOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to copy");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>;

  const slots = overview.slots || [];

  return (
    <div className="space-y-4" data-testid="signups-tab">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Week</div>
            <Select value={monday} onValueChange={setMonday}>
              <SelectTrigger className="w-64" data-testid="signups-week"><SelectValue /></SelectTrigger>
              <SelectContent>
                {weekWindowOptions(6, 16).map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Advisory</div>
            <Select value={advisoryFilter} onValueChange={setAdvisoryFilter}>
              <SelectTrigger className="w-44" data-testid="signups-advisory"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All students</SelectItem>
                {advisoryGroups.map((g) => (
                  <SelectItem key={g.group} value={g.group}>
                    {g.name || `Advisory ${g.group}`} ({g.student_count || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">View</div>
            <div className="flex">
              <Button variant={view === "grid" ? "default" : "outline"} size="sm" onClick={() => setView("grid")} className="rounded-r-none" data-testid="signups-view-grid">Grid</Button>
              <Button variant={view === "wizard" ? "default" : "outline"} size="sm" onClick={() => setView("wizard")} className="rounded-l-none" data-testid="signups-view-wizard">Wizard</Button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setSlotMgrOpen(true)} data-testid="signups-manage-slots">Manage Slots</Button>
          <Button variant="outline" size="sm" onClick={() => setCopyOpen(true)} data-testid="signups-copy-week">Copy last week's menu</Button>
          <Button size="sm" onClick={() => handleAddOption()} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="signups-add-option">+ Add Option</Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Options here stay in sync with the Master Schedule (classes marked "Optional — students sign up" appear automatically).
        Students already on an all-day trip are blocked from picking a block that day.
      </p>

      {slots.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">
          No sign-up slots yet. <Button variant="link" onClick={() => setSlotMgrOpen(true)}>Create your first slot</Button> (e.g., "Block 5") to get started.
        </CardContent></Card>
      ) : dates.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="signups-no-upcoming-days">
          No upcoming days this week. Sign-ups only show today and future days — pick a current or upcoming week above.
        </CardContent></Card>
      ) : view === "grid" ? (
        <GridView
          slots={slots} dates={dates} students={students}
          optionsByCell={optionsByCell} pickIndex={pickIndex} tripDays={tripDays} tripTitlesFor={tripTitlesFor}
          onPick={handlePick} onEditOption={handleEditOption} onAddOption={handleAddOption}
          countForOption={countForOption}
        />
      ) : (
        <WizardView
          slots={slots} dates={dates} students={students}
          optionsByCell={optionsByCell} pickIndex={pickIndex} tripDays={tripDays}
          onPick={handlePick} countForOption={countForOption}
        />
      )}

      <OptionEditorDialog
        open={editorOpen} onOpenChange={setEditorOpen}
        editing={editingOption}
        defaultType="block"
        allowTypeSwitch={false}
        defaults={editorDefaults}
        slots={slots} staff={staff} weekDates={dates}
        onSaved={(opts = {}) => {
          if (!opts.keepOpen) setEditorOpen(false);
          load();
        }}
      />
      <SlotManager open={slotMgrOpen} onOpenChange={setSlotMgrOpen} slots={slots} onChanged={load} />

      {copyOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setCopyOpen(false)}>
          <div className="bg-white rounded-lg p-4 max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold">Copy last week's menu</div>
            <p className="text-sm text-muted-foreground">This copies just the activity menu (titles, staff, capacities) into this week's matching days. Student picks are NOT copied — fresh slate as you requested.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCopyOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCopyLastWeek} className="bg-[#00305E]">Copy now</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Grid view: COLUMNS = students, ROWS = each slot×date (option menu) --------
function GridView({ slots, dates, students, optionsByCell, pickIndex, tripDays, tripTitlesFor, onPick, onEditOption, onAddOption, countForOption }) {
  if (students.length === 0) {
    return <Card><CardContent className="py-8 text-center text-muted-foreground">No students match this filter.</CardContent></Card>;
  }

  // Build rows: one per (date, slot) — chronological, slots ordered by their config order.
  const rows = [];
  dates.forEach((d) => {
    slots.forEach((slot) => {
      rows.push({ slot, date: d });
    });
  });

  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left font-medium sticky left-0 bg-gray-50 min-w-[200px] z-10 border-r">Date / Option</th>
              {students.map((s) => (
                <th key={s.id} className="px-2 py-2 text-center font-medium min-w-[110px] border-l align-bottom">
                  <div className="text-xs leading-tight">{(s.preferred_name || s.first_name)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ slot, date }) => {
              const cellOpts = optionsByCell[`${slot.id}|${date}`] || [];
              const onTrip = tripDays[date] || new Set();
              return (
                <tr key={`${slot.id}-${date}`} className="border-b hover:bg-gray-50/60" data-testid={`signup-row-${slot.id}-${date}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white border-r align-top min-w-[200px] z-10">
                    <div className="text-xs uppercase text-muted-foreground font-semibold">{slot.name}</div>
                    <div className="text-sm font-medium">{prettyDate(date)}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {cellOpts.map((o) => (
                        <Badge key={o.id} variant="outline" className="cursor-pointer text-[10px]" onClick={() => onEditOption(o)} data-testid={`signup-option-badge-${o.id}`}>
                          {o.title}{o.capacity != null && ` (${countForOption(o)}/${o.capacity})`}
                        </Badge>
                      ))}
                      <Badge variant="outline" className="cursor-pointer bg-blue-50 text-blue-700 text-[10px]" onClick={() => onAddOption(slot.id, date)} data-testid={`signup-add-cell-${slot.id}-${date}`}>+ Add</Badge>
                    </div>
                  </td>
                  {students.map((s) => {
                    const pick = pickIndex[`${s.id}|${slot.id}|${date}`];
                    if (onTrip.has(s.id)) {
                      return (
                        <td key={s.id} className="px-2 py-1 border-l text-center align-middle bg-amber-50" title={`On trip: ${tripTitlesFor(s.id, date)}`} data-testid={`signup-trip-block-${s.id}-${slot.id}-${date}`}>
                          <span className="text-[10px] text-amber-700 font-medium">🧳 Trip</span>
                        </td>
                      );
                    }
                    return (
                      <td key={s.id} className="px-2 py-1 border-l align-middle">
                        <Select
                          value={pick?.option_id || "none"}
                          onValueChange={(v) => onPick(s.id, slot.id, date, v, pick)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`pick-${s.id}-${slot.id}-${date}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            {cellOpts.map((o) => {
                              const used = countForOption(o);
                              const isFull = o.capacity != null && used >= o.capacity && pick?.option_id !== o.id;
                              return <SelectItem key={o.id} value={o.id}>{o.title}{o.capacity != null && ` (${used}/${o.capacity})`}{isFull && " ⚠"}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// -------- Wizard view: one student at a time --------
function WizardView({ slots, dates, students, optionsByCell, pickIndex, tripDays, onPick, countForOption }) {
  const [idx, setIdx] = useState(0);
  if (students.length === 0) {
    return <Card><CardContent className="py-8 text-center text-muted-foreground">No students match this filter.</CardContent></Card>;
  }
  const s = students[Math.min(idx, students.length - 1)];
  const completion = (slots.length * dates.length);
  const filled = slots.reduce((acc, slot) => acc + dates.filter((d) => pickIndex[`${s.id}|${slot.id}|${d}`]).length, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>{(s.preferred_name || s.first_name)}</CardTitle>
            <CardDescription>{s.advisory_group || "—"} · {filled} / {completion} slots picked</CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} data-testid="wizard-prev">← Prev</Button>
            <span className="text-xs text-muted-foreground">{idx + 1} / {students.length}</span>
            <Button size="sm" disabled={idx === students.length - 1} onClick={() => setIdx((i) => i + 1)} className="bg-[#00305E]" data-testid="wizard-next">Next →</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {slots.map((slot) => (
          <div key={slot.id}>
            <div className="text-xs font-semibold uppercase text-gray-700 mb-1">{slot.name}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
              {dates.map((d) => {
                const cellOpts = optionsByCell[`${slot.id}|${d}`] || [];
                const pick = pickIndex[`${s.id}|${slot.id}|${d}`];
                const onTrip = (tripDays[d] || new Set()).has(s.id);
                return (
                  <div key={d} className={`border rounded p-2 space-y-1 ${onTrip ? "bg-amber-50" : ""}`}>
                    <div className="text-xs font-medium text-muted-foreground">{prettyDate(d)}</div>
                    {onTrip ? (
                      <div className="text-xs text-amber-700 font-medium">🧳 On a trip</div>
                    ) : (<>
                      {cellOpts.length === 0 && <div className="text-xs text-gray-400">No options</div>}
                      {cellOpts.map((o) => {
                        const used = countForOption(o);
                        const isFull = o.capacity != null && used >= o.capacity && pick?.option_id !== o.id;
                        const selected = pick?.option_id === o.id;
                        return (
                          <button
                            key={o.id}
                            onClick={() => onPick(s.id, slot.id, d, selected ? "none" : o.id, pick)}
                            className={`w-full text-left text-xs px-2 py-1 rounded border transition ${selected ? "bg-[#00305E] text-white border-[#00305E]" : "bg-white hover:bg-gray-50"}`}
                            data-testid={`wizard-pick-${s.id}-${slot.id}-${d}-${o.id}`}
                          >
                            <div className="font-medium">{o.title}</div>
                            <div className={`text-[10px] ${selected ? "text-white/80" : "text-muted-foreground"}`}>
                              {o.lead_staff_name || ""}{o.capacity != null && ` · ${used}/${o.capacity}`}{isFull && " ⚠"}
                            </div>
                          </button>
                        );
                      })}
                    </>)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
